import { type ChangeEvent, type KeyboardEvent, type MouseEventHandler, type RefObject, useEffect, useRef } from "react";
import { styled } from "@pigment-css/react";
import { CrossLarge, QuickFilterIcon, SearchIcon } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { Pill } from "@acto/taskpane/field-sales-2/components/primitives/Pill";
import { IconButton } from "@acto/taskpane/field-sales-2/components/primitives/IconButton";
import { glassCapsulePress } from "@acto/taskpane/field-sales-2/styles/glassCapsulePress";

const CLEAR_BUTTON_MIN_CHARS = 8;

const SearchPill = styled(Pill)(({ theme }) => ({
  gap: theme.fieldSalesTwo.spacing[12],
  minWidth: 0,
  width: "100%",
}));

const InlineSearchIcon = styled("span")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: theme.fieldSalesTwo.spacing[20],
  height: theme.fieldSalesTwo.spacing[20],
  color: theme.fieldSalesTwo.colors.neutral[700],
  flex: "0 0 auto",
}));

const FilterIconBox = styled("span")(({ theme }) => ({
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: theme.fieldSalesTwo.spacing[20],
  height: theme.fieldSalesTwo.spacing[20],
}));

const ActiveFilterDot = styled("span")(({ theme }) => ({
  position: "absolute",
  border: "1px solid white",
  top: "1px",
  right: "-1px",
  width: "8px",
  height: "8px",
  borderRadius: theme.fieldSalesTwo.radius.circular,
  background: theme.fieldSalesTwo.colors.accent.action,
  pointerEvents: "none",
}));

const Input = styled("input")(({ theme }) => ({
  flex: "1 1 0",
  minWidth: 0,
  border: "none",
  background: "transparent",
  outline: "none",
  padding: 0,
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.input,
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
  "&::placeholder": {
    color: theme.fieldSalesTwo.colors.neutral[700],
    opacity: 1,
  },
  "&::-webkit-search-cancel-button, &::-webkit-search-decoration": {
    WebkitAppearance: "none",
    appearance: "none",
    display: "none",
  },
}));

const Separator = styled("span")(({ theme }) => ({
  display: "inline-block",
  width: theme.strokeWidth.regular,
  height: theme.fieldSalesTwo.spacing[20],
  backgroundColor: theme.fieldSalesTwo.colors.neutral[300],
  flex: "0 0 auto",
}));

const ClearIcon = () => (
  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10.8333 5.83333L5.83333 10.8333M10.8333 10.8333L5.83333 5.83333M16.0417 8.33333C16.0417 12.5905 12.5905 16.0417 8.33333 16.0417C4.07614 16.0417 0.625 12.5905 0.625 8.33333C0.625 4.07614 4.07614 0.625 8.33333 0.625C12.5905 0.625 16.0417 4.07614 16.0417 8.33333Z"
      stroke="#131313"
      strokeOpacity="0.64"
      strokeWidth="1.25"
      strokeLinecap="round"
    />
  </svg>
);

const CloseButtonRoot = styled("button")(({ theme }) => ({
  width: theme.fieldSalesTwo.spacing[50],
  height: theme.fieldSalesTwo.spacing[50],
  flex: "0 0 auto",
  padding: 0,
  margin: 0,
  border: "none",
  borderRadius: theme.fieldSalesTwo.radius.circular,
  background: theme.fieldSalesTwo.colors.surface.glassStrong,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  color: theme.fieldSalesTwo.colors.neutral[900],
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  ...glassCapsulePress(theme),
}));

interface SearchProps {
  placeholder: string;
  value?: string;
  onChange?: (value: string) => void;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onFilterClick?: () => void;
  filterButtonRef?: RefObject<HTMLButtonElement | null>;
  hasActiveFilters?: boolean;
  /** Blocks opening the search, e.g. when there is nothing to search through. */
  disabled?: boolean;
  /** Render the collapsed trigger as a bare icon (no glass pill), for inline list headers. */
  bare?: boolean;
  className?: string;
}

export function Search({
  placeholder,
  value,
  onChange,
  expanded,
  onExpandedChange,
  onFilterClick,
  filterButtonRef,
  hasActiveFilters = false,
  disabled = false,
  bare = false,
  className,
}: SearchProps) {
  const t = useTranslations("fieldSalesTwo.search");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (expanded) {
      inputRef.current?.focus();
    }
  }, [expanded]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      onExpandedChange(false);
    }
  };

  const handleClear = () => {
    onChange?.("");
    inputRef.current?.focus();
  };

  // Inline list headers show just the magnifying-glass icon at rest (no glass pill); it expands into
  // the full pill+input on tap.
  if (bare && !expanded) {
    return (
      <IconButton aria-label={t("open")} onClick={() => onExpandedChange(true)} disabled={disabled}>
        <SearchIcon />
      </IconButton>
    );
  }

  return (
    <SearchPill variant="action" className={className}>
      {expanded ? (
        <>
          <InlineSearchIcon aria-hidden="true">
            <SearchIcon />
          </InlineSearchIcon>
          <Input
            ref={inputRef}
            type="search"
            placeholder={placeholder}
            value={value ?? ""}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            aria-label={placeholder}
          />
          {(value?.length ?? 0) >= CLEAR_BUTTON_MIN_CHARS ? (
            <>
              <IconButton aria-label={t("clear")} onClick={handleClear}>
                <ClearIcon />
              </IconButton>
              {onFilterClick && <Separator aria-hidden="true" />}
            </>
          ) : null}
          {onFilterClick && (
            <IconButton ref={filterButtonRef} aria-label={t("filter")} onClick={onFilterClick}>
              <FilterIconBox>
                <QuickFilterIcon />
                {hasActiveFilters && <ActiveFilterDot aria-hidden="true" />}
              </FilterIconBox>
            </IconButton>
          )}
        </>
      ) : (
        <>
          <IconButton aria-label={t("open")} onClick={() => onExpandedChange(true)} disabled={disabled}>
            <SearchIcon />
          </IconButton>
          {onFilterClick && (
            <IconButton ref={filterButtonRef} aria-label={t("filter")} onClick={onFilterClick}>
              <FilterIconBox>
                <QuickFilterIcon />
                {hasActiveFilters && <ActiveFilterDot aria-hidden="true" />}
              </FilterIconBox>
            </IconButton>
          )}
        </>
      )}
    </SearchPill>
  );
}

interface SearchCloseButtonProps {
  onClick?: MouseEventHandler<HTMLButtonElement>;
  className?: string;
}

export function SearchCloseButton({ onClick, className }: SearchCloseButtonProps) {
  const t = useTranslations("fieldSalesTwo.search");
  return (
    <CloseButtonRoot type="button" aria-label={t("close")} onClick={onClick} className={className}>
      <CrossLarge />
    </CloseButtonRoot>
  );
}
